'use client';

import { useMemo, useState } from 'react';
import { GroupByOption, PaginatedResponse } from '@repo/types';
import { Card } from '@repo/ui';
import { useUpdateWriteOff } from '@xtnd/web/features/ar-collections/queries/writeOffsQuery';
import { DataTable } from '@xtnd/web/shared/components/DataTable';
import { QueryStringParams } from '@xtnd/web/shared';

import { WRITE_OFF_TABLE_DEFAULT_PROPS } from '../../constants';
import {
  DIALOG_MODE_MAP,
  UseWriteOffTabReturn,
  useWriteOffTab,
  WriteOffTabProps,
} from '../../hooks';
import { WriteOff, ArWriteOffFilter, WriteOffItem, FlatWriteOffItem } from '../../types/writeOffs';
import ActionDialog from './ActionDialog';
import ClientInvoicesTable from './ClientInvoicesTable';
import { WriteOffToolbar } from './FilterToolbars';
import { WriteOffTabHeader } from './WriteOffTabHeader';
import { WriteOffActivity } from './WriteOffActivity';

const config = {
  title: 'Proposed Write-Offs',
  description:
    'Invoices with balance < $100 or Appeal Status: Appeal Approved / Appeal Rejected / No Appeal / Deadline Expired',
  emptyState: {
    icon: 'HiClipboardList',
    title: 'No proposed write-offs',
    description: 'There are no proposed write-offs found.',
  },
  dialogIds: ['approve', 'escalated', 'note'],
  actions: [
    {
      icon: 'HiCheckCircle',
      iconColor: 'text-green-600',
      label: 'Approve',
      dialogId: 'approve',
    },
    {
      icon: 'HiXCircle',
      iconColor: 'text-red-600',
      label: 'Request Review',
      dialogId: 'escalated',
    },
    {
      icon: 'HiChat',
      iconColor: 'text-xtnd-blue',
      label: 'Comment',
      dialogId: 'note',
      showDividerBefore: true,
    },
  ],
  supportsLocalStateUpdate: true,
};

// ============================================================================
// Main Component
// ============================================================================

export default function ProposedTab({
  isAdmin,
  tableParams,
  setTableParams,
  data,
  isLoading,
  error,
  refetch,
}: WriteOffTabProps) {
  // Activity drawer state
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivityRow, setSelectedActivityRow] = useState<WriteOff | null>(null);

  const handleOpenActivity = (row: WriteOff) => {
    setSelectedActivityRow(row);
    setIsActivityOpen(true);
  };

  const tab = useWriteOffTab({
    tabType: 'proposed',
    isAdmin,
    tableParams,
    setTableParams,
    dialogIds: [...config.dialogIds],
    actions: [...config.actions],
    supportsLocalStateUpdate: config.supportsLocalStateUpdate,
    onOpenActivity: handleOpenActivity,
  });

  const { mutateAsync: patchWriteOff } = useUpdateWriteOff(
    tableParams,
    isAdmin,
    tab.handleLocalStateUpdate,
    tab.handleLocalCommentUpdate
  );

  const combinedLoading = isLoading;

  const filters = (tableParams.filters as ArWriteOffFilter) ?? {};
  const isDisplayInvoicesTable = !!filters.entityId && filters.groupBy === GroupByOption.CLIENT;

  return (
    <Card className="pt-0 mt-0">
      <div className="p-0 relative">
        {isDisplayInvoicesTable ? (
          <div className="py-4">
            <ClientInvoicesTable
              invoices={data?.data || []}
              clientName={tab.clientName}
              columns={tab.columns[GroupByOption.NONE]}
              onBackToClient={() => {
                // Remove entityId from filters
                const newFilters = { ...filters };
                delete newFilters.entityId;
                setTableParams({ ...tableParams, filters: newFilters, page: 1 });
                tab.resetInvoices();
              }}
              isDataTableLoading={isLoading}
              filters={tab.invoiceFilters}
              onFiltersChange={tab.setInvoiceFilters}
              getRowClassName={tab.getRowClassName}
              shouldFlatten={true}
            />
          </div>
        ) : (
          <>
            {/* Header Component */}
            <ProposedTabHeader tab={tab} isLoading={combinedLoading} onRefresh={refetch} />

            {/* Data Table Component */}
            <ProposedTabTable
              tab={tab}
              data={data}
              tableParams={tableParams}
              setTableParams={setTableParams}
              isLoading={combinedLoading}
              error={error}
              onRetry={refetch}
            />
          </>
        )}

        {/* Action Dialogs - rendered at top level so they work in both views */}
        {config.dialogIds.map(id => {
          const dialog = tab.dialogs[id];
          return (
            <ActionDialog
              key={id}
              isOpen={dialog?.isOpen ?? false}
              onClose={dialog?.close ?? (() => {})}
              writeOff={dialog?.selectedWriteOff ?? null}
              mode={DIALOG_MODE_MAP[id] ?? 'note'}
              onMutationStateChange={tab.handleMutationStateChange}
              onSubmit={patchWriteOff}
            />
          );
        })}

        {/* Activity Drawer */}
        <WriteOffActivity
          isOpen={isActivityOpen}
          onClose={() => {
            setIsActivityOpen(false);
            setSelectedActivityRow(null);
          }}
          invoiceNumber={selectedActivityRow?.invoiceNumber ?? ''}
          invoiceId={selectedActivityRow?.invoiceId ?? ''}
          tabType={tab.tabType}
        />
      </div>
    </Card>
  );
}

// ============================================================================
// Header Component
// ============================================================================

interface ProposedTabHeaderProps {
  tab: UseWriteOffTabReturn;
  isLoading: boolean;
  onRefresh: () => void;
}

function ProposedTabHeader({ tab, isLoading, onRefresh }: Readonly<ProposedTabHeaderProps>) {
  return (
    <WriteOffTabHeader
      title={config.title}
      description={config.description}
      isLoading={isLoading}
      onRefresh={onRefresh}
      renderToolbar={() => (
        <WriteOffToolbar
          isDataTableLoading={isLoading}
          filters={tab.filters}
          onChange={tab.handleChangeFilters}
        />
      )}
    />
  );
}

// ============================================================================
// Data Table Component
// ============================================================================

interface ProposedTabTableProps {
  tab: UseWriteOffTabReturn;
  data?: PaginatedResponse<WriteOff>;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  tableParams: QueryStringParams;
  setTableParams: (params: Partial<QueryStringParams>) => void;
}

function ProposedTabTable({
  tab,
  data,
  isLoading,
  setTableParams,
  tableParams,
  error,
  onRetry,
}: Readonly<ProposedTabTableProps>) {
  // Flatten data for table display
  const flatData = useMemo(() => {
    if (!data?.data) return [];
    if (tab.filters.groupBy === GroupByOption.CLIENT) return data.data;

    return data.data.flatMap(invoice => {
      const items = invoice.writeOffs;

      if (items?.length) {
        return items.map((item: WriteOffItem) => ({
          ...invoice, // Spread invoice-level data
          ...item, // Spread item-specific data (Category, Type, Amount)
          // Map item status to writeOffStatus for columns
          writeOffStatus: item.status,
          // Keep original references
          invoice: invoice,
          _parentInvoice: invoice,
          writeOff: item,
        })) as unknown as FlatWriteOffItem;
      }

      // Fallback
      return [
        {
          ...invoice,
          invoice: invoice,
          category: undefined,
          type: undefined,
          amount: invoice.writeOffAmount,
        } as unknown as FlatWriteOffItem,
      ];
    });
  }, [data, tab.filters.groupBy]);

  return (
    <>
      {/* Data Table */}
      <div className="mt-4">
        <DataTable
          {...WRITE_OFF_TABLE_DEFAULT_PROPS}
          columns={tab.columns[tab.filters.groupBy] ?? []}
          data={{
            data: flatData,
            total: data?.pagination?.total ?? 0,
          }}
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          onSort={sortBy => setTableParams({ ...tableParams, sorts: { ...sortBy } })}
          sorting={tableParams.sorts}
          pagination={data?.pagination}
          onPaginationChange={tab.handlePaginationChange}
          emptyState={config.emptyState}
          onRowClick={tab.handleRowClicked}
          getRowClassName={tab.getRowClassName}
          enableColumnResizing
          columnWidthsStorageKey={`writeoffs-proposed-table-${tab.filters.groupBy}`}
          // Removed isRowExpandable and renderExpandedRow
        />
      </div>
    </>
  );
}
